import { Component, effect, inject } from '@angular/core';
import { Router, RoutesRecognized } from '@angular/router';
import { isAssetTree } from '@models/components-asset-tree';
import { AssetTreeService } from '@services/asset-tree.service';
import { ElementService } from '@services/element.service';
import { SecurityGroupsService } from '@services/security-groups.service';
import { ElementsInfo } from '@shared/models/element';
import { sortBy } from 'lodash-es';
import { distinctUntilChanged, filter, take } from 'rxjs';
import { AppStateService } from '../app-state.service';
import { HeaderComponent } from '../header/header.component';
import { ExcludedUrlsPipe } from '../sidebar/excluded-urls.pipe';
import { SidebarComponent } from '../sidebar/sidebar.component';

@Component({
  selector: 'app-shell',
  imports: [SidebarComponent, HeaderComponent, ExcludedUrlsPipe],
  templateUrl: './shell.component.html',
  styleUrl: './shell.component.scss'
})
export class ShellComponent {
  private readonly appStateService = inject(AppStateService);
  protected readonly router = inject(Router);
  private readonly elementService = inject(ElementService);
  private readonly securityGroupService = inject(SecurityGroupsService);
  private readonly treeService = inject(AssetTreeService);

  constructor() {
    effect(onCleanup => {
      const element = this.appStateService.element();

      if (!element) {
        return;
      }

      this.appStateService.setEntity(
        element.elementId === ElementsInfo.PIPELINES.elementId ? 'pipeline' : 'installation'
      );

      const subscription = this.treeService.getInstallationByElementId(element.elementId).subscribe({
        next: installations => {
          const isTree = installations.some(asset => isAssetTree(asset));

          const sortedInstallations = isTree ? sortBy(installations, 'description') : installations;

          this.appStateService.setAvailableInstallations(sortedInstallations);

          if (sortedInstallations.length === 1) {
            this.appStateService.setInstallation(sortedInstallations[0]);
          }
        },
        error: err => {
          console.error(err);
          this.appStateService.setAvailableInstallations([]);
        }
      });

      onCleanup(() => {
        subscription.unsubscribe();
      });
    });
  }

  ngOnInit(): void {
    this.appStateService.setTransactionSite();

    this.appStateService
      .getUserObservable()
      .pipe(
        filter(user => !!user),
        take(1)
      )
      .subscribe(user => {
        this.elementService.getElementsForUser(user.userId).subscribe({
          next: elements => {
            this.appStateService.setAvailableElements(
              elements.filter(
                element =>
                  element.elementId === ElementsInfo.SURF.elementId ||
                  element.elementId === ElementsInfo.PIPELINES.elementId
              )
            );
          },
          error: err => {
            console.error(err);
            this.appStateService.setAvailableElements([]);
          }
        });

        this.securityGroupService.getSecurityGroupsForCurrentUser().subscribe({
          next: groups => {
            this.appStateService.setAdmin(groups.some(g => g.name.includes('Administrator')));
          },
          error: err => {
            console.error(err);
          }
        });
      });

    this.router.events
      .pipe(
        filter((e): e is RoutesRecognized => e instanceof RoutesRecognized),
        distinctUntilChanged()
      )
      .subscribe(data => {
        this.appStateService.setBreadcrumbs([]);
        if (data.state.root.children.length) {
          this.appStateService.setupBreadcrumbs(data.state.root.children);
        }
      });
  }
}
